import React, { useState } from "react";
import { cloneDeep, isEmpty } from "lodash";
import { CheckboxChangeEvent as ICheckboxChangeEvent } from "antd/es/checkbox";
import { SearchOutlined } from "@phonepe/enigma-ui-icons";
import { useEnigmaToken } from "@hooks/useEnigmaToken";
import { InputProps as IInputProps, Input } from "@components/input";
import { IFilterOption, TErrorMessages } from "../filterModal.types";
import { Checkbox } from "../../checkbox";
import {
    listItemCheckboxClass,
    StyledList,
    StyledListFilterWrapper,
    StyledListItem,
    StyledNoResultWrapper,
} from "../filterModal.styles";
import { FILTER_MODAL_DATA_IDS } from "../filterModal.constants";

export interface IListFiltersProps<T extends Record<keyof T, IFilterOption[]>> {
    isSingleFilterMode: boolean;
    mode?: "single" | "multiple";
    showSelectAll?: boolean;
    filterOptions: IFilterOption[];
    childFilters?: (keyof T)[];
    selectedFilterType: keyof T;
    selectedFilters: Partial<T>;
    setSelectedFilters: (selectedFilters: Partial<T>) => void;
    inputProps?: IInputProps;
    placeholder?: string;
    errorMessage?: Partial<TErrorMessages<T>>;
    helperText?: string;
}

const ListFilters = <T extends Record<keyof T, IFilterOption[]>>(
    props: IListFiltersProps<T>
) => {
    const {
        isSingleFilterMode,
        mode = "multiple",
        filterOptions = [],
        childFilters = [],
        selectedFilterType,
        selectedFilters,
        showSelectAll = true,
        setSelectedFilters,
        inputProps: parentInputProps = {},
        placeholder,
        errorMessage,
        helperText,
    } = props;
    const tokens = useEnigmaToken();

    const [searchInput, setSearchInput] = useState("");
    const isMultiSelectEnabled = mode === "multiple";

    const handleInputChange = (
        ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setSearchInput(ev.target?.value);
    };

    const handleOptionClick = (selectedOption: IFilterOption) => {
        let _updatedFilters = cloneDeep(selectedFilters);
        let _currentAppliedFilters: IFilterOption[] =
            _updatedFilters[selectedFilterType] ?? [];
        const currentOptionIndex = _currentAppliedFilters.findIndex(
            (ele) => ele.value === selectedOption.value
        );
        if (!isMultiSelectEnabled) {
            _currentAppliedFilters =
                currentOptionIndex === -1
                    ? ([selectedOption] as T[keyof T])
                    : ([] as IFilterOption[] as T[keyof T]);
        } else if (currentOptionIndex > -1) {
            _currentAppliedFilters?.splice(currentOptionIndex, 1);

            childFilters?.forEach((filter) => delete _updatedFilters[filter]);
        } else {
            _currentAppliedFilters?.push(selectedOption);
        }

        if (!isSingleFilterMode) {
            _updatedFilters[selectedFilterType] =
                _currentAppliedFilters as T[keyof T];
        } else {
            _updatedFilters = {
                [selectedFilterType]: _currentAppliedFilters,
            } as Partial<T>;
        }

        if (isEmpty(_currentAppliedFilters)) {
            delete _updatedFilters[selectedFilterType];
            childFilters?.forEach((filter) => delete _updatedFilters[filter]);
        }

        setSelectedFilters(_updatedFilters);
    };

    const allOptionsSelected =
        selectedFilters[selectedFilterType]?.length ===
        filterOptions.filter(
            (filter) =>
                !(filter.isDisabled as (filters: Partial<T>) => boolean)?.(
                    selectedFilters
                )
        )?.length;

    const onSelectAllChange = (ev: ICheckboxChangeEvent) => {
        const _updatedFilters = { ...selectedFilters };
        if (ev.target.checked) {
            _updatedFilters[selectedFilterType] = filterOptions.filter(
                (fil) =>
                    !(fil.isDisabled as (filters: Partial<T>) => boolean)?.(
                        selectedFilters
                    )
            ) as T[keyof T];
        } else {
            delete _updatedFilters[selectedFilterType];
        }
        setSelectedFilters(_updatedFilters);
    };

    const _getSearchValue = (value: string) => `Your search "${value}" did
                                not match any result. Please try again.`;
    const renderListWithSearch = () => {
        const _searchInput = searchInput?.toLowerCase();
        const optionsRenderer = filterOptions
            .map((optionItem) => {
                const _optionSearchString =
                    optionItem.searchText?.toLowerCase();
                const isSelected = selectedFilters[selectedFilterType]?.find(
                    (opt) => opt.value === optionItem.value
                );

                if (
                    !_searchInput ||
                    (_searchInput &&
                        _optionSearchString?.includes(_searchInput))
                ) {
                    const disabledState =
                        !isSelected &&
                        (
                            optionItem.isDisabled as (
                                filters: Partial<T>
                            ) => boolean
                        )?.(selectedFilters);
                    return (
                        <StyledListItem
                            data-testid={
                                FILTER_MODAL_DATA_IDS.AUTOCOMPLETE_OPTION
                            }
                            tokens={tokens}
                            active={Boolean(isSelected)}
                            disabled={disabledState}
                            key={optionItem.key}
                        >
                            <Checkbox
                                data-testid={
                                    FILTER_MODAL_DATA_IDS.AUTOCOMPLETE_INPUT
                                }
                                onChange={() => handleOptionClick(optionItem)}
                                disabled={disabledState}
                                checked={Boolean(isSelected)}
                                className={listItemCheckboxClass(tokens)}
                                value={optionItem.value}
                            >
                                {optionItem.title}
                            </Checkbox>
                        </StyledListItem>
                    );
                }
            })
            .filter((opt) => opt);

        if (isEmpty(optionsRenderer)) {
            return (
                <StyledNoResultWrapper
                    data-testid={
                        FILTER_MODAL_DATA_IDS.AUTOCOMPLETE_NOT_FOUND_WRAPPER
                    }
                    tokens={tokens}
                >
                    <SearchOutlined width={30} height={30} />
                    {searchInput ? (
                        <>
                            <div>
                                <strong>{searchInput}</strong> not found.
                            </div>
                            <div>{_getSearchValue(searchInput)}</div>
                        </>
                    ) : (
                        "No result found."
                    )}
                </StyledNoResultWrapper>
            );
        }

        return (
            <StyledList
                showFooter={Boolean(
                    errorMessage?.[selectedFilterType] ?? helperText
                )}
            >
                {optionsRenderer}
            </StyledList>
        );
    };

    const inputProps: IInputProps = {
        id: "inlineSearch",
        size: "large",
        placeholder: placeholder ?? "Search...",
        allowClear: true,
        disabled: isEmpty(filterOptions),
        style: { borderRadius: 0, borderTop: 0, borderLeft: 0, borderRight: 0 },
        value: searchInput,
        onChange: handleInputChange,
        prefix: <SearchOutlined width={22} height={22} />,
        ...parentInputProps,
    };
    return (
        <StyledListFilterWrapper
            data-testid={FILTER_MODAL_DATA_IDS.AUTOCOMPLETE_WRAPPER}
        >
            <Input {...inputProps} />
            {showSelectAll && isMultiSelectEnabled && !searchInput && (
                <Checkbox
                    value="all"
                    data-testid={FILTER_MODAL_DATA_IDS.CHECKBOX_SELECT_ALL}
                    onChange={onSelectAllChange}
                    className={listItemCheckboxClass(tokens)}
                    checked={allOptionsSelected}
                >
                    Select All
                </Checkbox>
            )}
            {renderListWithSearch()}
        </StyledListFilterWrapper>
    );
};

export default ListFilters;
